import { useState, type JSX } from "react";
import type {
  AdminApprovalRequest,
  AdminCaseUpdate,
  AdminPendingCaseDto,
  AdminReviewChecklist,
  CaseGenerationBatchDto,
  Difficulty,
  GameUserDto,
  GenerateCasesRequest,
  TaxonomySectorDto,
} from "../api/types";
import { AdminGeneratePanel } from "./AdminGeneratePanel";
import { AdminGenerationBatches } from "./AdminGenerationBatches";
import { AdminUsersPanel } from "./AdminUsersPanel";

interface AdminReviewPageProps {
  cases: readonly AdminPendingCaseDto[];
  users: readonly GameUserDto[];
  taxonomy: readonly TaxonomySectorDto[];
  generationBatches: readonly CaseGenerationBatchDto[];
  isBusy: boolean;
  error: string | null;
  refreshError: string | null;
  generateMessage: string | null;
  onGenerate: (request: GenerateCasesRequest) => Promise<void>;
  onSetRole: (userId: string, role: GameUserDto["role"]) => Promise<void>;
  onSave: (caseId: string, update: AdminCaseUpdate) => Promise<void>;
  onReject: (caseId: string, reason: string) => Promise<void>;
  onApprove: (caseId: string, approval: AdminApprovalRequest) => Promise<void>;
  onLogout: () => void;
}

const emptyChecklist: AdminReviewChecklist = {
  legalAccuracy: false,
  sourceSupport: false,
  realisticScenario: false,
  pedagogicalProgression: false,
  bokmal: false,
  noPii: false,
  spoilerFreeContent: false,
};

const checklistLabels: ReadonlyArray<{
  key: keyof AdminReviewChecklist;
  label: string;
}> = [
  { key: "legalAccuracy", label: "Jussen er faglig korrekt" },
  { key: "sourceSupport", label: "Kildene støtter alle juridiske påstander" },
  { key: "realisticScenario", label: "Rollen og scenarioet er realistiske" },
  { key: "pedagogicalProgression", label: "Den pedagogiske progresjonen er god" },
  { key: "bokmal", label: "Språket er korrekt bokmål" },
  { key: "noPii", label: "Innholdet inneholder ikke personopplysninger" },
  { key: "spoilerFreeContent", label: "Spillerinnhold og hint røper ikke svaret" },
];

function isChecklistComplete(checklist: AdminReviewChecklist): boolean {
  return Object.values(checklist).every(Boolean);
}

function createUpdate(gameCase: AdminPendingCaseDto): AdminCaseUpdate {
  return {
    scenario: gameCase.scenario,
    learningObjective: gameCase.learningObjective,
    difficulty: gameCase.difficulty,
    hints: [...gameCase.hints],
  };
}

function resolveSelectedId(
  cases: readonly AdminPendingCaseDto[],
  selectedId: string | null,
): string | null {
  if (selectedId !== null && cases.some((gameCase) => gameCase.id === selectedId))
    return selectedId;

  return cases[0]?.id ?? null;
}

/** Provides a minimal, auditable workflow for reviewing generated cases. */
export function AdminReviewPage({
  cases,
  users,
  taxonomy,
  generationBatches,
  isBusy,
  error,
  refreshError,
  generateMessage,
  onGenerate,
  onSetRole,
  onSave,
  onReject,
  onApprove,
  onLogout,
}: AdminReviewPageProps): JSX.Element {
  const [selectedId, setSelectedId] = useState<string | null>(cases[0]?.id ?? null);
  const effectiveId = resolveSelectedId(cases, selectedId);
  const selectedCase = cases.find((gameCase) => gameCase.id === effectiveId) ?? null;
  const [draftId, setDraftId] = useState<string | null>(effectiveId);
  const [update, setUpdate] = useState<AdminCaseUpdate | null>(
    selectedCase === null ? null : createUpdate(selectedCase),
  );
  const [checklist, setChecklist] = useState<AdminReviewChecklist>(emptyChecklist);
  const [rejectReason, setRejectReason] = useState("");
  const [auditNote, setAuditNote] = useState("");

  if (draftId !== effectiveId) {
    setDraftId(effectiveId);
    setUpdate(selectedCase === null ? null : createUpdate(selectedCase));
    setChecklist(emptyChecklist);
    setRejectReason("");
    setAuditNote("");
  }

  const selectCase = (gameCase: AdminPendingCaseDto): void => {
    if (gameCase.id === effectiveId)
      return;

    setSelectedId(gameCase.id);
    setDraftId(gameCase.id);
    setUpdate(createUpdate(gameCase));
    setChecklist(emptyChecklist);
    setRejectReason("");
    setAuditNote("");
  };

  const setChecklistItem = (key: keyof AdminReviewChecklist, value: boolean): void => {
    setChecklist((current) => ({ ...current, [key]: value }));
  };

  const setHint = (index: 0 | 1 | 2, value: string): void => {
    setUpdate((current) => {
      if (current === null)
        return null;
      const hints: [string, string, string] = [...current.hints];
      hints[index] = value;
      return { ...current, hints };
    });
  };

  const canApprove = isChecklistComplete(checklist) && auditNote.trim().length > 0;

  return (
    <main className="admin-page">
      <header className="admin-toolbar">
        <div>
          <p className="eyebrow">Administrasjon</p>
          <h1>Faglig kontroll av caser</h1>
        </div>
        <div className="choices">
          <a className="button-link secondary" href="/">Til spillet</a>
          <button type="button" className="secondary" onClick={onLogout}>Logg ut</button>
        </div>
      </header>

      {error !== null && <p role="alert">{error}</p>}
      {refreshError !== null && (
        <p className="generation-status" role="status">
          Automatisk oppdatering feilet. Viser sist hentede status.
        </p>
      )}
      <AdminGeneratePanel
        taxonomy={taxonomy}
        isBusy={isBusy}
        message={generateMessage}
        onGenerate={onGenerate}
      />
      <AdminGenerationBatches batches={generationBatches} />
      <AdminUsersPanel
        users={users}
        isBusy={isBusy}
        onSetRole={onSetRole}
      />
      {cases.length === 0 && !isBusy && <p>Det er ingen caser som venter på kontroll.</p>}

      <div className="admin-layout">
        <nav aria-label="Caser som venter">
          <ul className="pending-list">
            {cases.map((gameCase) => (
              <li key={gameCase.id}>
                <button
                  type="button"
                  className={effectiveId === gameCase.id ? "selected" : "choice-card"}
                  onClick={() => { selectCase(gameCase); }}
                >
                  <strong>{gameCase.title}</strong>
                  <span>{gameCase.topicName} · {gameCase.difficulty}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {selectedCase !== null && update !== null && (
          <article className="review-card">
            <header>
              <p className="eyebrow">{selectedCase.sector} / {selectedCase.topicName}</p>
              <h2>{selectedCase.title}</h2>
              <p>{selectedCase.caseType}</p>
            </header>

            <section>
              <h3>Scenario</h3>
              <label htmlFor="scenario">Rediger scenario</label>
              <textarea
                id="scenario"
                rows={5}
                value={update.scenario}
                onChange={(event) => {
                  setUpdate({ ...update, scenario: event.target.value });
                }}
              />

              <h3>Læringsmål</h3>
              <label htmlFor="objective">Rediger læringsmål</label>
              <textarea
                id="objective"
                rows={3}
                value={update.learningObjective}
                onChange={(event) => {
                  setUpdate({ ...update, learningObjective: event.target.value });
                }}
              />

              <label htmlFor="difficulty">Vanskelighetsgrad</label>
              <select
                id="difficulty"
                value={update.difficulty}
                onChange={(event) => {
                  setUpdate({ ...update, difficulty: event.target.value as Difficulty });
                }}
              >
                <option value="Easy">Lett</option>
                <option value="Medium">Middels</option>
                <option value="Hard">Krevende</option>
              </select>
            </section>

            <section>
              <h3>Portvokter</h3>
              <p><strong>{selectedCase.gatekeeper.persona}:</strong> {selectedCase.gatekeeper.openingPosition}</p>
            </section>

            <section className="restricted">
              <h3>Skjult vurderingsrubrikk</h3>
              <ul>
                {selectedCase.rubric.map((criterion) => (
                  <li key={criterion.criterionId}>
                    <strong>{criterion.weight} %:</strong> {criterion.description}
                  </li>
                ))}
              </ul>
              <h4>Påkrevde resonnementer</h4>
              <ul>
                {selectedCase.requiredReasoningCriteria.map((criterion) => (
                  <li key={criterion.criterionId}>{criterion.description}</li>
                ))}
              </ul>
            </section>

            <section>
              <h3>Hintprogresjon</h3>
              {update.hints.map((hint, index) => (
                <label key={index}>
                  Hint {index + 1}
                  <textarea
                    rows={2}
                    value={hint}
                    onChange={(event) => {
                      setHint(index as 0 | 1 | 2, event.target.value);
                    }}
                  />
                </label>
              ))}
              <button
                type="button"
                className="secondary"
                disabled={isBusy}
                onClick={() => { void onSave(selectedCase.id, update); }}
              >
                Lagre endringer
              </button>
            </section>

            <section>
              <h3>Frosne kilder</h3>
              <ul className="source-list">
                {selectedCase.sources.map((source) => (
                  <li key={source.documentId}>
                    <a href={source.url} rel="noreferrer" target="_blank">{source.title}</a>
                    <code>{source.documentId}</code>
                    <blockquote>{source.excerpt}</blockquote>
                  </li>
                ))}
              </ul>
              <div className="quality-flags">
                <span className={selectedCase.qualityFlags.isGeneratedContractValid ? "pass" : "fail"}>
                  {selectedCase.qualityFlags.isGeneratedContractValid
                    ? "Genereringskontrakt godkjent"
                    : "Genereringskontrakten er ugyldig"}
                </span>
                <span className={selectedCase.qualityFlags.isGrounded ? "pass" : "fail"}>
                  {selectedCase.qualityFlags.isGrounded ? "Forankring godkjent" : "Forankring mangler"}
                </span>
                <span className={selectedCase.qualityFlags.isBokmal ? "pass" : "fail"}>
                  {selectedCase.qualityFlags.isBokmal ? "Bokmål godkjent" : "Bokmål må rettes"}
                </span>
              </div>
            </section>

            <section>
              <h3>Revisjonsspor</h3>
              <dl>
                <dt>Status</dt>
                <dd>{selectedCase.audit.status}</dd>
                {selectedCase.audit.auditNote !== null && (
                  <>
                    <dt>Revisjonsnotat</dt>
                    <dd>{selectedCase.audit.auditNote}</dd>
                  </>
                )}
                {selectedCase.audit.rejectionReason !== null && (
                  <>
                    <dt>Avvisningsgrunn</dt>
                    <dd>{selectedCase.audit.rejectionReason}</dd>
                  </>
                )}
              </dl>
            </section>

            <section>
              <h3>Kontrolliste</h3>
              <div className="checklist">
                {checklistLabels.map((item) => (
                  <label key={item.key}>
                    <input
                      type="checkbox"
                      checked={checklist[item.key]}
                      onChange={(event) => { setChecklistItem(item.key, event.target.checked); }}
                    />
                    {item.label}
                  </label>
                ))}
              </div>

              <label htmlFor="audit-note">Revisjonsnotat</label>
              <textarea
                id="audit-note"
                rows={3}
                value={auditNote}
                onChange={(event) => { setAuditNote(event.target.value); }}
              />
              <button
                type="button"
                disabled={isBusy || !canApprove}
                onClick={() => {
                  void onApprove(selectedCase.id, {
                    checklist,
                    auditNote: auditNote.trim(),
                  });
                }}
              >
                Godkjenn og aktiver
              </button>
            </section>

            <section className="danger-zone">
              <h3>Avvis case</h3>
              <label htmlFor="reject-reason">Begrunnelse for avvisning</label>
              <textarea
                id="reject-reason"
                rows={3}
                value={rejectReason}
                onChange={(event) => { setRejectReason(event.target.value); }}
              />
              <button
                type="button"
                className="danger"
                disabled={isBusy || rejectReason.trim().length === 0}
                onClick={() => { void onReject(selectedCase.id, rejectReason.trim()); }}
              >
                Avvis case
              </button>
            </section>
          </article>
        )}
      </div>
    </main>
  );
}
